import React, { useRef, useState } from 'react';
import { Group, Panel, Separator } from 'react-resizable-panels';
import { GripHorizontal, GripVertical } from 'lucide-react';
import { useMediaQuery } from 'hooks/use-media-query';
import { cn } from 'lib/utils';

interface Props {
  /** Accessible name of the divider. */
  handleLabel: string;
  /** Left pane on desktop, top pane below `lg`. */
  start: React.ReactNode;
  /** Right pane on desktop, bottom pane below `lg`. */
  end: React.ReactNode;
}

/** Neither pane can be dragged smaller than this, so both stay usable. */
const MIN_PANE = '20%';

/**
 * The divider can be grabbed 12px either side of its line, within the panes' padding.
 * Matches the invisible band drawn around the line (`before:-inset-*-3`): a finger that
 * lands beside the line must still land on the divider, or the pane under it starts
 * scrolling, cancels the drag and leaves the divider stuck.
 */
const GRAB_AREA = { coarse: 24, fine: 24 };

/** Side by side from `lg`, and on a phone held sideways, where stacked panes would leave the answer a few lines. */
const SIDE_BY_SIDE = '(min-width: 1024px), (min-width: 640px) and (max-height: 520px)';

/**
 * Two exam panes split 50/50 by a draggable divider: side by side from `lg`
 * or on a phone held sideways (drag left and right), stacked below it (drag up and down). Arrow keys move
 * the divider in 5% steps, and a double-click puts it back in the middle.
 */
export function ResizableSplit({ handleLabel, start, end }: Props) {
  const isDesktop = useMediaQuery(SIDE_BY_SIDE);
  const orientation = isDesktop ? 'horizontal' : 'vertical';
  const Grip = isDesktop ? GripVertical : GripHorizontal;
  // The library focuses the divider when it is grabbed, which browsers count as
  // keyboard focus. Keep the focus ring for keyboard use only, as elsewhere in the app.
  const [grabbed, setGrabbed] = useState(false);
  const separatorRef = useRef<HTMLDivElement>(null);

  return (
    // Keyed by orientation: crossing the breakpoint starts again from 50/50.
    <Group
      key={orientation}
      orientation={orientation}
      resizeTargetMinimumSize={GRAB_AREA}
      className="min-h-0 min-w-0 flex-1"
      // Runs after the library has focused the divider, even when the grab landed just beside it.
      onPointerDown={() => setGrabbed(document.activeElement === separatorRef.current)}
    >
      <Panel defaultSize="50%" minSize={MIN_PANE} className="flex min-w-0 flex-col">
        {start}
      </Panel>
      <Separator
        elementRef={separatorRef}
        aria-label={handleLabel}
        onKeyDown={() => setGrabbed(false)}
        onBlur={() => setGrabbed(false)}
        className={cn(
          // Above both panes, so the grip is not painted over by the second one.
          'relative z-10 touch-none bg-gray-200 transition-colors',
          'data-[separator=hover]:bg-gray-400 data-[separator=active]:bg-primary-600',
          'before:absolute',
          isDesktop ? 'w-px before:-inset-x-3 before:inset-y-0' : 'h-px before:-inset-y-3 before:inset-x-0',
          grabbed && 'focus-visible:!outline-none',
        )}
      >
        {/* Styled from the divider's state with `[[data-separator=…]_&]:`, not `group-data-*`:
            the web build's postcss-selector-parser 6.1.3 drops every group-/peer- variant. */}
        <span
          aria-hidden
          className={cn(
            'absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-500 shadow-sm transition-colors',
            '[[data-separator=hover]_&]:border-gray-900 [[data-separator=hover]_&]:text-gray-900',
            '[[data-separator=active]_&]:border-gray-900 [[data-separator=active]_&]:bg-gray-900 [[data-separator=active]_&]:text-white',
            isDesktop ? 'h-10 w-4' : 'h-4 w-10',
          )}
        >
          <Grip className="h-3.5 w-3.5" />
        </span>
      </Separator>
      <Panel defaultSize="50%" minSize={MIN_PANE} className="flex min-w-0 flex-col">
        {end}
      </Panel>
    </Group>
  );
}

export default ResizableSplit;
